import type { AuthStatusResponse, SessionResponse, SettingsPayload } from '@budget/shared';
import { createContext, type ReactNode, use, useCallback, useEffect, useState } from 'react';
import { api, onAuthLost, setAccessToken } from '../api/client';
import { getDeviceId, getDeviceName, setDeviceName } from '../api/device';

interface Session {
  user: { id: string; email: string };
  device: { id: string; name: string };
  settings: SettingsPayload;
}

interface AuthState {
  status: 'checking' | 'anonymous' | 'authenticated';
  /** Faux tant qu'aucun compte n'existe : on propose alors la création. */
  bootstrapped: boolean;
  session: Session | null;
  signIn: (email: string, password: string, deviceName: string) => Promise<void>;
  bootstrap: (email: string, password: string, deviceName: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const value = use(AuthContext);
  if (value === null) throw new Error('useAuth doit être utilisé dans AuthProvider');
  return value;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthState['status']>('checking');
  const [bootstrapped, setBootstrapped] = useState(true);
  const [session, setSession] = useState<Session | null>(null);

  const adopt = useCallback((response: SessionResponse) => {
    setAccessToken(response.accessToken);
    setDeviceName(response.device.name);
    setSession({ user: response.user, device: response.device, settings: response.settings });
    setStatus('authenticated');
  }, []);

  // Au démarrage : le cookie de rafraîchissement suffit-il à nous reconnaître ?
  // C'est ce qui évite de ressaisir un mot de passe à chaque ouverture.
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const restored = await api<SessionResponse>('/auth/refresh', {
          method: 'POST',
          anonymous: true,
        });
        if (!cancelled) adopt(restored);
        return;
      } catch {
        // Pas de session : on regarde s'il faut proposer une création de compte.
      }

      try {
        const state = await api<AuthStatusResponse>('/auth/status', { anonymous: true });
        if (!cancelled) setBootstrapped(state.bootstrapped);
      } catch {
        // Hors ligne au démarrage : on affiche l'écran de connexion malgré tout.
      }
      if (!cancelled) setStatus('anonymous');
    })();

    return () => {
      cancelled = true;
    };
  }, [adopt]);

  useEffect(() => {
    onAuthLost(() => {
      setSession(null);
      setStatus('anonymous');
    });
  }, []);

  const authenticate = useCallback(
    async (path: '/auth/login' | '/auth/bootstrap', email: string, password: string, deviceName: string) => {
      const response = await api<SessionResponse>(path, {
        method: 'POST',
        anonymous: true,
        body: { email, password, deviceId: getDeviceId(), deviceName },
      });
      adopt(response);
    },
    [adopt],
  );

  const value: AuthState = {
    status,
    bootstrapped,
    session,
    signIn: (email, password, deviceName) =>
      authenticate('/auth/login', email, password, deviceName),
    bootstrap: (email, password, deviceName) =>
      authenticate('/auth/bootstrap', email, password, deviceName),
    signOut: async () => {
      try {
        await api<void>('/auth/logout', { method: 'POST' });
      } finally {
        setAccessToken(null);
        setSession(null);
        setStatus('anonymous');
      }
    },
  };

  return <AuthContext value={value}>{children}</AuthContext>;
}

export { getDeviceName };
